import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyGetAssessmentsAnswersQuery } from 'services/assessmentsService';
import { selectActivityId, selectAssessmentsId } from '../assessmentsAuthSlice';
import {
  selectQuestionIdToNumberMap,
  setSkippedQuestions,
  setPreviouslyAnsweredQuestionIds,
} from '../assessmentsSlice';
import { getAssessmentsAnsweredState } from '../utils/assessmentsQuestionUtils';
import { useAssessmentsParams } from './useAssessmentsParams';
import {
  getAnsweredQuestionIds,
  transformQuestionnaireResponseToFormValues,
} from '../utils/assessmentsAnswersUtils';
import { useAssessmentsQuestions } from './useAssessmentsQuestions';
import { useAssessmentsContext } from './useAssessmentsContext';
import { useAssessmentsPages } from './useAssessmentsPages';
import {
  generateSkipMap,
  getSkippedQuestionsFromAnswers,
} from '../utils/assessmentsSkiplogic.utils';
import { useAssessmentsSkipLogic } from './useAssessmentsSkipLogic';

export const useAssessmentsLoadAnswers = () => {
  const dispatch = useDispatch();
  const assessmentsId = useSelector(selectAssessmentsId);
  const activityId = useSelector(selectActivityId);
  const { tenantId } = useAssessmentsParams();
  const { questions } = useAssessmentsQuestions();
  const questionIdToNumberMap = useSelector(selectQuestionIdToNumberMap);
  const {
    formMethods: { reset },
    pendingScrollTarget,
  } = useAssessmentsContext();
  const { resetPageData } = useAssessmentsPages();
  const [
    getAssessmentsAnswers,
    {
      data: assessmentsAnswersResponse,
      isLoading: assessmentsAnswersLoading,
      error: assessmentsAnswersError,
    },
  ] = useLazyGetAssessmentsAnswersQuery();
  const { jumpToQuestion } = useAssessmentsSkipLogic();

  const loadAssessmentsAnswers = useCallback(() => {
    if (!tenantId || !assessmentsId || !activityId) {
      return;
    }
    getAssessmentsAnswers({ tenantId, surveyId: assessmentsId, activityId }).then(({ data }) => {
      const transformedAnswers = transformQuestionnaireResponseToFormValues({
        questionIdToNumberMap,
        questions,
        assessmentsAnswersResponse: data,
      });

      const skipMap = generateSkipMap(questions ?? []);
      const initialSkips = getSkippedQuestionsFromAnswers({
        questions: questions ?? [],
        formValues: transformedAnswers,
        skipMap,
        questionIdToNumberMap,
      });

      dispatch(setSkippedQuestions(initialSkips));
      dispatch(setPreviouslyAnsweredQuestionIds(getAnsweredQuestionIds(transformedAnswers)));

      const { firstUnanswered, atleastOneAnswered } = getAssessmentsAnsweredState(
        questions ?? [],
        transformedAnswers,
        initialSkips,
      );

      if (firstUnanswered && atleastOneAnswered) {
        jumpToQuestion(firstUnanswered.id);
      }
      // Reset form with loaded answers as default values
      // This marks the form as not dirty since we just loaded from server
      reset(transformedAnswers, {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
      });
      resetPageData(transformedAnswers, initialSkips);
    });
  }, [
    tenantId,
    assessmentsId,
    activityId,
    getAssessmentsAnswers,
    questionIdToNumberMap,
    questions,
    reset,
    resetPageData,
    dispatch,
    pendingScrollTarget,
  ]);

  useEffect(() => {
    loadAssessmentsAnswers();
  }, [loadAssessmentsAnswers]);

  return {
    assessmentsAnswersResponse,
    assessmentsAnswersLoading,
    assessmentsAnswersError,
    refetchAssessmentsAnswers: loadAssessmentsAnswers,
  };
};
